import { Course, Section, Lesson, LessonContent, LessonType, createEmptyLessonContent, Tag } from '../types';
import { SlideBasedContent, createEmptySlideContent, isSlideBasedContent } from '../components/course-builder/slides/slideTypes';
import { supabase } from '../lib/supabase';
import { repairLessonContent, validateLessonContent } from '../lib/scenario-builder/compositor/validateLessonContent';
import { reportError } from '../lib/errorHandling';
import { enrollmentService } from './enrollmentService';
import { activityService } from './activityService';

// Union type for all content types
type AnyLessonContent = LessonContent | SlideBasedContent;

/**
 * Convert nested course_tags(tag:tags(*, category:tag_categories(sort_order)))
 * rows into a flat Tag[] in category order, then tag order. Tag sort_order is a
 * per-category sequence, so sorting by it alone interleaves categories
 * arbitrarily — every card showed its chips in a different order.
 */
function mapCourseTags(rows: any): Tag[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row: any) => row?.tag)
    .filter(Boolean)
    .map((t: any) => ({
      id: t.id,
      categoryId: t.category_id,
      tenantId: t.tenant_id,
      name: t.name,
      color: t.color ?? undefined,
      sortOrder: t.sort_order ?? 0,
      categorySortOrder: t.category?.sort_order ?? 0,
    }))
    .sort(
      (a, b) =>
        a.categorySortOrder - b.categorySortOrder ||
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name),
    )
    .map(({ categorySortOrder: _cso, ...tag }) => tag);
}

/**
 * Resume at the first uncompleted non-HEADING lesson in sequential order.
 * Falls back to the last lesson if every lesson is complete.
 */
export function getResumeLessonId(
  course: Pick<Course, 'sections'>,
  completedLessonIds: string[]
): string | null {
  const completed = new Set(completedLessonIds);
  const allLessons = course.sections
    .flatMap(s => [...s.lessons].sort((a, b) => a.order - b.order))
    .filter(l => l.type !== LessonType.HEADING);
  if (allLessons.length === 0) return null;
  const firstUncompleted = allLessons.find(l => !completed.has(l.id));
  return firstUncompleted?.id ?? allLessons[allLessons.length - 1]?.id ?? null;
}

/**
 * Map raw `lessons(*)` rows to `Lesson[]`, honouring the working-draft vs
 * published-snapshot model.
 *  - `'draft'`: `content` = live draft; also surfaces `publishedContent` so the
 *    builder can diff draft against snapshot.
 *  - `'published'`: `content` = snapshot (`published_content`); non-HEADING
 *    lessons with no snapshot (never published) are dropped so unpublished
 *    drafts never reach students.
 */
function mapLessonRows(
  rows: any[],
  mode: 'draft' | 'published',
  completedIds?: string[],
): Lesson[] {
  const completed = completedIds ?? [];
  return (rows || [])
    .slice()
    .sort((a: any, b: any) => a.order - b.order)
    .filter((lesson: any) =>
      mode === 'draft' ||
      lesson.type === LessonType.HEADING ||
      lesson.published_content != null,
    )
    .map((lesson: any) => ({
      id: lesson.id,
      sectionId: lesson.section_id,
      title: lesson.title,
      type: lesson.type,
      duration: lesson.duration,
      content: mode === 'published' ? lesson.published_content : lesson.content,
      ...(mode === 'draft' ? { publishedContent: lesson.published_content ?? null } : {}),
      isCompleted: completed.includes(lesson.id),
      isLocked: lesson.is_locked,
      order: lesson.order,
    }));
}

/**
 * Fetch published lesson rows from the column-restricted `published_lesson_content`
 * view, grouped by section id. Student-facing reads MUST go through the view, not
 * the `lessons` table — the view exposes `published_content` only, never the draft
 * `content` column (see migration 20260609000004). Returns rows shaped for
 * `mapLessonRows(..., 'published')`.
 */
async function fetchPublishedLessonsBySection(sectionIds: string[]): Promise<Record<string, any[]>> {
  const bySection: Record<string, any[]> = {};
  if (sectionIds.length === 0) return bySection;

  const { data, error } = await supabase
    .from('published_lesson_content')
    .select('id, section_id, title, type, duration, is_locked, "order", published_content')
    .in('section_id', sectionIds);

  if (error) throw new Error(error.message);

  for (const lesson of data ?? []) {
    (bySection[lesson.section_id] ??= []).push(lesson);
  }
  return bySection;
}

/**
 * Publish gate: a course must have a real title before students can see it.
 * Creation is deliberately frictionless (empty titles fall back to "Untitled
 * Course"), so the naming requirement is enforced here — the last door out.
 * Used by both courseService.publishCourse and adminService.publishCourse.
 */
export async function assertCourseTitled(courseId: string): Promise<void> {
  const { data, error } = await supabase
    .from('courses')
    .select('title')
    .eq('id', courseId)
    .maybeSingle();

  if (error) throw new Error(error.message || 'Failed to load course');

  const title = (data?.title ?? '').trim();
  if (!title || /^untitled course$/i.test(title)) {
    throw new Error('Give your course a title before publishing.');
  }
}

export const courseService = {
  // --- DELEGATED DOMAINS ---
  // Enrollment + progress live in services/enrollmentService.ts; embed/activity/
  // submission tracking in services/activityService.ts. Spread here so existing
  // `courseService.x()` call sites keep working — NEW code should import the
  // focused service directly.
  ...enrollmentService,
  ...activityService,

  // --- READ OPERATIONS ---

  /** Lightweight title lookup (admin/instructor report headers). */
  async getCourseTitle(id: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('courses')
      .select('title')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data?.title ?? null;
  },

  async getAllCourses(tenantId?: string): Promise<Course[]> {
    let query = supabase
      .from('courses')
      .select(`
        *,
        instructor:users!instructor_id(name),
        sections(*),
        course_tags(tag:tags(*, category:tag_categories(sort_order)))
      `)
      .eq('published', true);

    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query.order('sort_order', { ascending: true });

    if (error) throw new Error(error.message);

    // Student-facing: pull lesson content from the published-only view, never the
    // lessons table directly (which holds the draft `content`).
    const allSectionIds = data.flatMap((course: any) => (course.sections || []).map((s: any) => s.id));
    const lessonsBySection = await fetchPublishedLessonsBySection(allSectionIds);

    return data.map(course => ({
      id: course.id,
      instructorId: course.instructor_id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: course.instructor?.name || 'Unknown',
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating,
      points: course.points ?? 100,
      certificateEnabled: course.certificate_enabled ?? false,
      showFacilitator: course.show_facilitator ?? false,
      markingsIncluded: course.markings_included ?? null,
      markingsPerQuestion: course.markings_per_question ?? null,
      tags: mapCourseTags(course.course_tags),
      contentLoaded: true,
      sections: (course.sections || [])
        .sort((a: any, b: any) => a.order - b.order)
        .map((section: any) => ({
          id: section.id,
          courseId: section.course_id,
          title: section.title,
          order: section.order,
          // Student-facing: serve the published snapshot, hide unpublished lessons.
          lessons: mapLessonRows(lessonsBySection[section.id] ?? [], 'published')
        }))
    }));
  },

  async getCourseById(id: string, opts?: { content?: 'draft' | 'published' }): Promise<Course | undefined> {
    const mode = opts?.content ?? 'draft';
    // Draft mode (builder/instructor) reads the live `content` straight off the
    // lessons table. Published mode (students) must NOT touch that column, so it
    // omits the lessons embed and pulls content from the column-restricted view.
    const lessonsEmbed = mode === 'draft' ? 'sections(*, lessons(*))' : 'sections(*)';
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        instructor:users!instructor_id(name),
        ${lessonsEmbed},
        course_tags(tag:tags(*, category:tag_categories(sort_order)))
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return undefined;
      throw new Error(error.message);
    }

    const { data: { user } } = await supabase.auth.getUser();
    let enrollment = null;

    if (user) {
      const { data: enrollmentData } = await supabase
        .from('enrollments')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', id)
        .maybeSingle();

      enrollment = enrollmentData;
    }

    // Published mode pulls lesson content from the published-only view (keyed by
    // section). Draft mode already has lessons embedded off the table.
    const lessonsBySection = mode === 'published'
      ? await fetchPublishedLessonsBySection((data.sections || []).map((s: any) => s.id))
      : null;

    return {
      id: data.id,
      instructorId: data.instructor_id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      instructorName: data.instructor?.name ?? 'Unknown',
      price: data.price,
      category: data.category,
      level: data.level,
      totalStudents: data.total_students,
      rating: data.rating,
      points: data.points ?? 100,
      published: data.published,
      certificateEnabled: data.certificate_enabled ?? false,
      showFacilitator: data.show_facilitator ?? false,
      markingsIncluded: data.markings_included ?? null,
      markingsPerQuestion: data.markings_per_question ?? null,
      completionBadgeId: data.completion_badge_id ?? undefined,
      tags: mapCourseTags(data.course_tags),
      contentLoaded: true,
      sections: data.sections.map((section: any) => ({
        id: section.id,
        courseId: section.course_id,
        title: section.title,
        order: section.order,
        lessons: mapLessonRows(
          mode === 'published' ? (lessonsBySection![section.id] ?? []) : section.lessons,
          mode,
          enrollment?.completed_lesson_ids ?? [],
        )
      }))
    };
  },

  // --- WRITE OPERATIONS ---

  async updateCourse(updatedCourse: Course): Promise<Course> {
    const { data, error } = await supabase
      .from('courses')
      .update({
        title: updatedCourse.title,
        description: updatedCourse.description,
        thumbnail_url: updatedCourse.thumbnailUrl,
        price: updatedCourse.price,
        category: updatedCourse.category,
        level: updatedCourse.level,
        points: updatedCourse.points ?? 100,
        course_type: updatedCourse.courseType ?? 'standard',
        certificate_enabled: updatedCourse.certificateEnabled ?? false,
        show_facilitator: updatedCourse.showFacilitator ?? false,
        markings_included: updatedCourse.markingsIncluded ?? null,
        markings_per_question: updatedCourse.markingsPerQuestion ?? null,
        completion_badge_id: updatedCourse.completionBadgeId ?? null
      })
      .eq('id', updatedCourse.id)
      .select(`
        *,
        instructor:users!instructor_id(name)
      `)
      .single();

    if (error) throw new Error(error.message);

    return {
      id: data.id,
      instructorId: data.instructor_id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      instructorName: data.instructor?.name ?? 'Unknown',
      price: data.price,
      category: data.category,
      level: data.level,
      totalStudents: data.total_students,
      rating: data.rating,
      points: data.points ?? 100,
      certificateEnabled: data.certificate_enabled ?? false,
      showFacilitator: data.show_facilitator ?? false,
      markingsIncluded: data.markings_included ?? null,
      markingsPerQuestion: data.markings_per_question ?? null,
      completionBadgeId: data.completion_badge_id ?? undefined,
      sections: []
    };
  },

  async createCourse(newCourse: Partial<Course>): Promise<Course> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch user's tenant_id for RLS compliance
    const { data: profile } = await supabase
      .from('users')
      .select('tenant_id')
      .eq('id', user.id)
      .single();

    const { data, error } = await supabase
      .from('courses')
      .insert({
        instructor_id: user.id,
        tenant_id: profile?.tenant_id,
        title: newCourse.title!,
        description: newCourse.description!,
        thumbnail_url: newCourse.thumbnailUrl || 'https://via.placeholder.com/400x300?text=Course',
        price: newCourse.price || 0,
        category: newCourse.category || 'General',
        level: newCourse.level || 'Beginner',
        points: newCourse.points ?? 100,
        published: false
      })
      .select(`
        *,
        instructor:users!instructor_id(name)
      `)
      .single();

    if (error) throw new Error(error.message);

    const createdCourse: Course = {
      id: data.id,
      instructorId: data.instructor_id,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnail_url,
      instructorName: data.instructor?.name ?? 'Unknown',
      price: data.price,
      category: data.category,
      level: data.level,
      totalStudents: data.total_students,
      rating: data.rating,
      points: data.points ?? 100,
      certificateEnabled: data.certificate_enabled ?? false,
      showFacilitator: data.show_facilitator ?? false,
      markingsIncluded: data.markings_included ?? null,
      markingsPerQuestion: data.markings_per_question ?? null,
      completionBadgeId: data.completion_badge_id ?? undefined,
      sections: []
    };

    // Auto-create a default section for the flat lesson list
    const defaultSection = await courseService.createSection(createdCourse.id, '');
    createdCourse.sections = [defaultSection];

    return createdCourse;
  },

  async deleteCourse(courseId: string): Promise<void> {
    const { error } = await supabase
      .from('courses')
      .delete()
      .eq('id', courseId);

    if (error) throw new Error(error.message || 'Failed to delete course');

    // Verify deletion succeeded (RLS can silently block with no error)
    const { data: check } = await supabase.from('courses').select('id').eq('id', courseId).maybeSingle();
    if (check) throw new Error('Course could not be deleted. You may not have permission.');
  },

  // --- SECTION CRUD ---

  async createSection(courseId: string, title: string): Promise<Section> {
    // Get the highest order number for existing sections
    const { data: existingSections } = await supabase
      .from('sections')
      .select('order')
      .eq('course_id', courseId)
      .order('order', { ascending: false })
      .limit(1);

    const nextOrder = existingSections && existingSections.length > 0 ? existingSections[0].order + 1 : 0;

    const { data, error } = await supabase
      .from('sections')
      .insert({
        course_id: courseId,
        title,
        order: nextOrder
      })
      .select()
      .single();

    if (error) throw new Error(error.message);

    return {
      id: data.id,
      courseId: data.course_id,
      title: data.title,
      order: data.order,
      lessons: []
    };
  },

  async updateSection(sectionId: string, updates: Partial<Pick<Section, 'title' | 'order'>>): Promise<Section> {
    const { data, error } = await supabase
      .from('sections')
      .update({
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.order !== undefined && { order: updates.order })
      })
      .eq('id', sectionId)
      .select(`*, lessons(*)`)
      .single();

    if (error) throw new Error(error.message);

    return {
      id: data.id,
      courseId: data.course_id,
      title: data.title,
      order: data.order,
      lessons: (data.lessons || []).map((lesson: any) => ({
        id: lesson.id,
        sectionId: lesson.section_id,
        title: lesson.title,
        type: lesson.type,
        duration: lesson.duration,
        content: lesson.content,
        isCompleted: false,
        isLocked: lesson.is_locked,
        order: lesson.order
      }))
    };
  },

  async deleteSection(sectionId: string): Promise<void> {
    const { error } = await supabase
      .from('sections')
      .delete()
      .eq('id', sectionId);

    if (error) throw new Error(error.message);
  },

  // --- LESSON CRUD ---

  async createLesson(sectionId: string, title: string, type: LessonType = LessonType.TEXT): Promise<Lesson> {
    // Get the highest order number for existing lessons in this section
    const { data: existingLessons } = await supabase
      .from('lessons')
      .select('order')
      .eq('section_id', sectionId)
      .order('order', { ascending: false })
      .limit(1);

    const nextOrder = existingLessons && existingLessons.length > 0 ? existingLessons[0].order + 1 : 0;

    // Headings have no content; regular lessons use slide-based content (v2)
    const isHeading = type === LessonType.HEADING;
    const emptyContent = isHeading ? null : createEmptySlideContent();

    const { data, error } = await supabase
      .from('lessons')
      .insert({
        section_id: sectionId,
        title,
        type,
        duration: isHeading ? 0 : 5,
        content: emptyContent,
        is_locked: false,
        order: nextOrder
      })
      .select()
      .single();

    if (error) throw new Error(error.message);

    return {
      id: data.id,
      sectionId: data.section_id,
      title: data.title,
      type: data.type,
      duration: data.duration,
      content: data.content,
      isCompleted: false,
      isLocked: data.is_locked,
      order: data.order
    };
  },

  async updateLesson(lessonId: string, updates: Partial<Pick<Lesson, 'title' | 'type' | 'duration' | 'content' | 'isLocked' | 'order'>>): Promise<Lesson> {
    const updateData: Record<string, any> = {};

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.duration !== undefined) updateData.duration = updates.duration;
    if (updates.content !== undefined) updateData.content = updates.content;
    if (updates.isLocked !== undefined) updateData.is_locked = updates.isLocked;
    if (updates.order !== undefined) updateData.order = updates.order;

    const { data, error } = await supabase
      .from('lessons')
      .update(updateData)
      .eq('id', lessonId)
      .select();

    if (error) throw new Error(error.message);
    if (!data || data.length === 0) throw new Error('Failed to update: lesson not found or insufficient permissions');

    const row = data[0];
    return {
      id: row.id,
      sectionId: row.section_id,
      title: row.title,
      type: row.type,
      duration: row.duration,
      content: row.content,
      isCompleted: false,
      isLocked: row.is_locked,
      order: row.order
    };
  },

  async deleteLesson(lessonId: string): Promise<void> {
    const { error } = await supabase
      .from('lessons')
      .delete()
      .eq('id', lessonId);

    if (error) throw new Error(error.message);
  },

  async reorderLessons(sectionId: string, lessonIds: string[]): Promise<void> {
    // One RPC = one UPDATE with unnest(), instead of a parallel PATCH per
    // lesson. The per-row version fired N concurrent statements per drag,
    // which saturated the connection pool and — under CPU pressure — was the
    // reliable trigger for statement-timeout storms in the builder.
    const { error } = await supabase.rpc('reorder_lessons', {
      p_section_id: sectionId,
      p_lesson_ids: lessonIds,
    });
    if (error) throw new Error(error.message);
  },

  // --- CURRICULUM BATCH SAVE ---

  async saveLessonContent(lessonId: string, content: AnyLessonContent): Promise<void> {
    // Scenario-group integrity gate (Phase 5d). Slide-based lessons get
    // their scenarioGroups validated against position + combo rules before
    // they hit the DB. Throws ScenarioGroupValidationError on hard violations
    // so the caller can render specifics. No-op for legacy lessons + lessons
    // with no scenario groups bound.
    //
    // Residue (orphaned bindings, group entries whose slides were deleted) is
    // REPAIRED rather than blocked: it renders as nothing, the author cannot
    // see or fix it, and hard-blocking on it used to wedge the whole lesson —
    // every save failed, including the edit that would have fixed it.
    let toSave = content;
    if (isSlideBasedContent(content)) {
      const { content: repaired, repairs } = repairLessonContent(content);
      if (repairs.length > 0) {
        toSave = repaired;
        reportError('scenarioGroupRepair', new Error(repairs.join('; ')), { lessonId });
      }
      validateLessonContent(repaired);
    }

    const { data, error } = await supabase
      .from('lessons')
      .update({ content: toSave })
      .eq('id', lessonId)
      .select('id');

    if (error) throw new Error(error.message);
    if (!data || data.length === 0) throw new Error('Failed to save: lesson not found or insufficient permissions');
  },

  // --- COURSE PUBLISH ---

  async duplicateCourse(courseId: string): Promise<Course> {
    // Fetch the source course with all sections and lessons
    const source = await courseService.getCourseById(courseId);
    if (!source) throw new Error('Source course not found');

    // Create the new course
    const newCourse = await courseService.createCourse({
      title: `${source.title} (Copy)`,
      description: source.description,
      thumbnailUrl: source.thumbnailUrl,
      price: source.price,
      category: source.category,
      level: source.level,
      points: source.points,
    });

    // Duplicate lessons in the flat list (newCourse already has a default section)
    const defaultSection = newCourse.sections[0];
    const allLessons = source.sections.flatMap(s => [...s.lessons].sort((a, b) => a.order - b.order));
    for (const lesson of allLessons) {
      const newLesson = await courseService.createLesson(defaultSection.id, lesson.title, lesson.type);
      if (lesson.content && typeof lesson.content === 'object') {
        await courseService.saveLessonContent(newLesson.id, lesson.content as AnyLessonContent);
      }
    }

    // Re-fetch to get the full course with all nested data
    const result = await courseService.getCourseById(newCourse.id);
    if (!result) throw new Error('Failed to fetch duplicated course');
    return result;
  },

  async publishCourse(courseId: string, published: boolean): Promise<void> {
    // First publish snapshots the current draft so students immediately see the
    // content the instructor just made live. Unpublish only flips visibility —
    // the snapshot is left intact so re-publishing restores the last live state.
    if (published) {
      await assertCourseTitled(courseId);
      await courseService.publishCourseContent(courseId);
    }

    const { error } = await supabase
      .from('courses')
      .update({ published })
      .eq('id', courseId);

    if (error) throw new Error(error.message || 'Failed to update course');

    // Verify update succeeded (RLS can silently block with no error)
    const { data: check } = await supabase.from('courses').select('id, published').eq('id', courseId).maybeSingle();
    if (check && check.published !== published) throw new Error('Course could not be updated. You may not have permission.');
  },

  /**
   * Snapshot the course's current draft lesson content as the published version
   * (copies `lessons.content` -> `lessons.published_content` for all non-HEADING
   * lessons). This is what students subsequently read. Used by both first
   * publish and "Publish changes".
   */
  async publishCourseContent(courseId: string): Promise<void> {
    const { error } = await supabase.rpc('publish_course_content', { p_course_id: courseId });
    if (error) throw new Error(error.message || 'Failed to publish course content');
  },

  // --- INSTRUCTOR COURSES ---

  async getInstructorCourses(tenantId?: string): Promise<Course[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch ALL courses (published and unpublished) — RLS enforces instructor access.
    //
    // Lesson METADATA only: `lessons(*)` would pull the `content` JSONB — every
    // slide of every lesson of every course in the tenant — making this the
    // heaviest query in the app (measured 419ms/815ms per call in prod). The
    // surfaces this feeds (InstructorCourses, InstructorDashboard, CourseDetail's
    // curriculum list, WelcomePage's resume link, useLessonNavigation's prev/next)
    // read titles, order, type and counts, never content. Courses returned here
    // are marked `contentLoaded: false` so anything that does need to render a
    // lesson refetches via `getCourseById(id, { content: 'draft' })`.
    let query = supabase
      .from('courses')
      .select(`
        *,
        instructor:users!instructor_id(name),
        sections(
          *,
          lessons(id, section_id, title, duration, type, is_locked, "order")
        ),
        course_tags(tag:tags(*, category:tag_categories(sort_order)))
      `);

    if (tenantId) query = query.eq('tenant_id', tenantId);

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('courseService.getInstructorCourses Supabase error', {
        message: error.message,
        code: (error as any).code,
        details: (error as any).details,
        hint: (error as any).hint,
        tenantId,
        userId: user.id,
      });
      throw new Error(error.message);
    }

    return data.map(course => ({
      id: course.id,
      instructorId: course.instructor_id,
      title: course.title,
      description: course.description,
      thumbnailUrl: course.thumbnail_url,
      instructorName: course.instructor?.name ?? 'Unknown',
      price: course.price,
      category: course.category,
      level: course.level,
      totalStudents: course.total_students,
      rating: course.rating,
      points: course.points ?? 100,
      published: course.published,
      certificateEnabled: course.certificate_enabled ?? false,
      showFacilitator: course.show_facilitator ?? false,
      markingsIncluded: course.markings_included ?? null,
      markingsPerQuestion: course.markings_per_question ?? null,
      tags: mapCourseTags(course.course_tags),
      // Metadata-only read (see the select above) — consumers must not render
      // `lesson.content` off these rows.
      contentLoaded: false,
      sections: (course.sections || [])
        .sort((a: any, b: any) => a.order - b.order)
        .map((section: any) => ({
          id: section.id,
          courseId: section.course_id,
          title: section.title,
          order: section.order,
          lessons: (section.lessons || [])
            .sort((a: any, b: any) => a.order - b.order)
            .map((lesson: any) => ({
              id: lesson.id,
              sectionId: lesson.section_id,
              title: lesson.title,
              type: lesson.type,
              duration: lesson.duration,
              content: '',
              isCompleted: false,
              isLocked: lesson.is_locked,
              order: lesson.order
            }))
        }))
    }));
  },

};
